import {
  ContractTxAsyncActionResponse,
  FarmContractContainer,
} from 'common/models/crypto';
import { StorageUtils } from './storageUtils';
import omit from 'lodash/omit';
import { Nullable } from 'common/models/optional';
import { IchiContract, MintableToken } from 'constants/contracts';
import { getBlockNumber } from '@wagmi/core';
import { Address, TransactionReceipt } from 'viem';
import { getPublicClient } from '@wagmi/core';
import { wagmiConf } from 'containers/WalletProvider/WalletProvider';

export namespace TransactionUtils {
  type CheckAndHandleTxArgs = {
    // TODO: Once implementing the deposit/withdraw we'll need to update this type
    contractContainer:
      | FarmContractContainer
      | MintableToken
      | IchiContract<any>;
  } & StorageUtils.AccountAddressArg &
    StorageUtils.ChainIdArg &
    StorageUtils.FunctionNameArg;

  export async function checkAndHandleTx(
    args: CheckAndHandleTxArgs
  ): Promise<ContractTxAsyncActionResponse> {
    const response: ContractTxAsyncActionResponse = {
      hash: null,
      output: null,
      value: null,
      receipt: null,
    };

    const storedTx = await StorageUtils.getTx({
      ...args,
    });

    if (storedTx) {
      response.value = storedTx.value;
      const publicClient = getPublicClient(wagmiConf);

      const receipt = await publicClient.waitForTransactionReceipt({
        hash: storedTx.hash as Address,
      });

      // Null out the hash to indicate no active tx
      response.hash = null;

      // Ensure to remove the tx so it's no longer checked in the machines
      // NOTE: THIS DOES NOT!!! remove the hash at this point under certain circumstances
      // Ultimately see txMachine actions where we clean up the stored txs
      // TODO: Remove and vet this section after converting everything to the new txMachine
      await StorageUtils.removeTxByHash({
        hash: storedTx.hash,
        ...args,
      });

      if (receipt.status === 'success') {
        // success
        // Set output to true for successful minting
        response.output = 'Success!';
        return response;
      }
      throw new Error('Transaction failed!');
    }

    return response;
  }

  // TODO: I think I can remove this because I don't need to pass the contract container here at all, I just need to create a provider
  // and wait for trx based on that, but I NEED to keep in mind how this would work with polygon, which I'm not sure yet
  type WaitForStoredTxArgs = StorageUtils.ContractContainer &
    StorageUtils.StoredTxArg;
  export async function waitForStoredTx(
    args: WaitForStoredTxArgs
  ): Promise<ContractTxAsyncActionResponse> {
    if (!args.storedTx) {
      const message = 'No stored tx given';
      console.error(message);
      throw new Error(message);
    }

    if (!args.contractContainer) {
      const message = `No contract given for stored tx with hash: ${args.storedTx.hash}`;
      console.error(message);
      throw new Error(message);
    }

    let receipt: Nullable<TransactionReceipt> = null;
    try {
      const publicClient = getPublicClient(wagmiConf);
      receipt = await publicClient.waitForTransactionReceipt({
        hash: args.storedTx.hash as Address,
      });
    } catch (e) {
      console.warn(e);
    }

    const response: ContractTxAsyncActionResponse = {
      ...args.storedTx,
      output: null,
      value: null,
      receipt,
    };

    // NOTE: THIS DOES NOT!!! remove the hash at this point under certain circumstances
    // Ultimately see txMachine actions where we clean up the stored txs
    // TODO: Remove and vet this section after converting everything to the new txMachine
    await StorageUtils.removeTxByHash({
      hash: args.storedTx.hash,
      accountAddress: args.storedTx.functionName,
      chainId: args.storedTx.chainId,
    });

    if (receipt?.status === 'success') {
      // success
      // Set output to true for successful minting
      response.output = 'Success!';
      return response;
    }

    console.error('Transaction failed', receipt);
    throw new Error('Transaction failed!');
  }

  type WaitForTxArgs = StorageUtils.StoredTxArg & {
    accountAddress: Nullable<string>;
  };
  export async function waitForTx(
    args: WaitForTxArgs
  ): Promise<ContractTxAsyncActionResponse> {
    if (!args.storedTx) {
      const message = 'No stored tx given';
      console.error(message);
      throw new Error(message);
    }

    if (!args.accountAddress) {
      const message = 'No account address given';
      console.error(message);
      throw new Error(message);
    }

    let receipt: Nullable<TransactionReceipt> = null;
    try {
      const publicClient = getPublicClient(wagmiConf);
      receipt = await publicClient.waitForTransactionReceipt({
        hash: args.storedTx.hash as Address,
        confirmations: 1,
        onReplaced: (replacement) => {
          void (async () => {
            console.warn(
              `It appears tx: ${args.storedTx.hash} has been replaced with: ${replacement.replacedTransaction.hash}`
            );
            // Go ahead and remove the current hash
            await StorageUtils.removeTxByHash({
              hash: args.storedTx.hash,
              accountAddress: args.storedTx.functionName,
              chainId: args.storedTx.chainId,
            });

            // Store the new tx hash and re-call the above method.

            const replacedTxStartBlock = await getBlockNumber(wagmiConf);

            const replacedStoredTx: StorageUtils.StoredTx = {
              hash: replacement.replacedTransaction.hash,
              functionName: args.storedTx.functionName,
              labels: args.storedTx.labels,
              value: args.storedTx.value,
              chainId: args.storedTx.chainId,
              accountAddress: args.accountAddress as string,
              // localforace won't serialize the wait
              txReceipt: omit(replacement.replacedTransaction, 'wait'),
              txStartBlock: replacedTxStartBlock,
            };
            // Recursively call this function
            return waitForTx({
              accountAddress: args.accountAddress,
              storedTx: replacedStoredTx,
            });
          })();
        },
      });
    } catch (error) {
      console.error('Error waiting for tx in viem', error);
    }

    const response: ContractTxAsyncActionResponse = {
      ...args.storedTx,
      output: null,
      value: null,
      receipt,
    };

    // NOTE: THIS DOES NOT!!! remove the hash at this point under certain circumstances
    // Ultimately see txMachine actions where we clean up the stored txs
    // TODO: Remove and vet this section after converting everything to the new txMachine
    await StorageUtils.removeTxByHash({
      hash: args.storedTx.hash,
      accountAddress: args.storedTx.functionName,
      chainId: args.storedTx.chainId,
    });

    if (receipt?.status === 'success') {
      // success
      // Set output to true for successful minting
      response.output = 'Success!';
      return response;
    }

    console.error('Transaction failed', receipt);
    throw new Error('Transaction failed!');
  }

  type HandlePostContractTxArgs = {
    tx: any;
  } & StorageUtils.AccountAddressArg &
    StorageUtils.ChainIdArg &
    StorageUtils.FunctionNameArg &
    StorageUtils.LabelsArg &
    StorageUtils.ValueArg;
  export async function handlePostContractTx(
    args: HandlePostContractTxArgs
  ): Promise<ContractTxAsyncActionResponse> {
    const response: ContractTxAsyncActionResponse = {
      hash: null,
      output: null,
      value: null,
      receipt: null,
    };

    await StorageUtils.storeTx({
      hash: args.tx.hash,
      ...args,
    });

    response.hash = args.tx.hash;
    response.value = args.value;

    return response;
  }

  // old
  type HasTxArgs = {
    accountAddress: string;
    chainId: number;
    functionName: string;
  };
  export async function hasTx(args: HasTxArgs): Promise<boolean> {
    try {
      if (args.accountAddress && args.chainId) {
        const storedTx = await StorageUtils.getTx(args);
        if (storedTx) {
          switch (storedTx.functionName) {
            case args.functionName:
              return true;
            default:
              throw new Error(`No existing ${storedTx.functionName} tx found`);
          }
        }
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
    throw new Error(`No existing ${args.functionName} tx found`);
  }

  type GetStoredTxArgs = {
    accountAddress: string;
    chainId: number;
    functionName: string;
  };
  export async function getStoredTx(
    args: GetStoredTxArgs
  ): Promise<StorageUtils.StoredTx> {
    try {
      if (args.accountAddress && args.chainId) {
        const storedTx = await StorageUtils.getTx(args);
        if (storedTx) {
          switch (storedTx.functionName) {
            case args.functionName:
              return storedTx;
            default:
              throw new Error('No existing stored tx found');
          }
        }
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
    throw new Error(`No existing ${args.functionName} stored tx found`);
  }

  // type HasTxByHashArgs = {
  //   accountAddress: string;
  //   chainId: number;
  //   hash: string;
  // };
  // export async function hasTxByHash(args: HasTxByHashArgs): Promise<boolean> {
  //   if (!args.accountAddress || !args.chainId || !args.hash) {
  //     const message = `Could not look up stored transaction, insufficient arguments`;
  //     console.error(message, args);
  //     throw new Error(message);
  //   }
  //   try {
  //     const storedTx = await StorageUtils.getTxByHash(args);
  //     if (!storedTx) {
  //       throw new Error(`No stored transaction found with hash ${args.hash}`);
  //     }
  //     return true;
  //   } catch (e) {
  //     console.error(e);
  //     throw e;
  //   }
  // }
}
